import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { Users, RefreshCw, UserX, X, AlertTriangle, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatRelativeTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  const now = Date.now();
  const diffMs = date.getTime() - now;
  const absDiff = Math.abs(diffMs);

  if (absDiff < 60_000) return "just now";
  if (absDiff < 3_600_000) {
    const mins = Math.round(absDiff / 60_000);
    return diffMs > 0 ? `in ${mins}m` : `${mins}m ago`;
  }
  if (absDiff < 86_400_000) {
    const hrs = Math.round(absDiff / 3_600_000);
    return diffMs > 0 ? `in ${hrs}h` : `${hrs}h ago`;
  }
  const days = Math.round(absDiff / 86_400_000);
  return diffMs > 0 ? `in ${days}d` : `${days}d ago`;
}

function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ActiveUsersDialog({ open, onOpenChange, onPrevious, onNext }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [kickTarget, setKickTarget] = useState(null); // session to confirm kick
  const [kicking, setKicking] = useState(false);
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const intervalRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/users/active-sessions");
      setSessions(data);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open, then auto-refresh every 30s
  useEffect(() => {
    if (!open) {
      clearInterval(intervalRef.current);
      return;
    }
    fetchSessions();
    intervalRef.current = setInterval(fetchSessions, 5_000);
    return () => clearInterval(intervalRef.current);
  }, [open, fetchSessions]);

  const handleKickConfirm = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      await api.delete(`/users/sessions/${kickTarget.session_token}`);
      setSessions((prev) =>
        prev.filter((s) => s.session_token !== kickTarget.session_token)
      );
      setKickTarget(null);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to kick session");
      setKickTarget(null);
    } finally {
      setKicking(false);
    }
  };

  const handleClearAllConfirm = async () => {
    setClearingAll(true);
    try {
      await api.delete("/users/sessions");
      setSessions([]);
      setClearAllConfirm(false);
      window.location.reload();
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to clear all sessions");
      setClearAllConfirm(false);
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <>
      <SimpleDialog open={open} onOpenChange={onOpenChange} className="max-w-lg">
        <div
          className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 rounded-none text-white w-full max-h-[85vh] flex flex-col"
          data-testid="active-users-dialog"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-orange-500/15 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 border border-orange-500/40 flex items-center justify-center bg-orange-500/5">
                <Users className="w-3.5 h-3.5 text-orange-400" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-orange-400 mb-0.5">
                  Admin
                </div>
                <div className="font-display font-bold text-base tracking-tight text-slate-200">
                  Active Sessions
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setClearAllConfirm(true)}
                disabled={loading || sessions.length === 0}
                title="Clear all sessions"
                aria-label="Clear all sessions"
                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-orange-500/40 text-[11px] tracking-widest uppercase text-orange-400 hover:border-orange-500 hover:bg-orange-500/10 transition-colors disabled:opacity-40"
                data-testid="clear-all-sessions-button"
              >
                <Trash2 className="w-3 h-3" />
                Clear All
              </button>
              <button
                onClick={fetchSessions}
                disabled={loading}
                title="Refresh"
                aria-label="Refresh sessions"
                className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors disabled:opacity-40"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              {onPrevious && (
                <button
                  onClick={onPrevious}
                  aria-label="Previous panel"
                  title="Who's Online"
                  className="flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              {onNext && (
                <button
                  onClick={onNext}
                  aria-label="Next panel"
                  title="Settings"
                  className="flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className="flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {error && (
              <div className="flex items-center gap-2 px-4 py-3 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-4">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                {error}
              </div>
            )}

            {loading && sessions.length === 0 ? (
              <div
                className="text-orange-400 text-xs tracking-widest uppercase animate-pulse py-8 text-center"
                data-testid="active-users-loading"
              >
                Loading sessions…
              </div>
            ) : sessions.length === 0 ? (
              <div className="border border-dashed border-slate-700 p-10 text-center text-slate-500 text-sm">
                No active sessions found.
              </div>
            ) : (
              <div className="space-y-0" data-testid="active-users-list">
                {/* Table header */}
                <div className="grid grid-cols-[2fr_1.5fr_auto] gap-2 px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-slate-600 border-b border-slate-800">
                  <span>User</span>
                  <span>Last Seen</span>
                  <span className="w-16 text-right">Action</span>
                </div>

                {sessions.map((s) => (
                  <div
                    key={s.session_token}
                    className="grid grid-cols-[2fr_1.5fr_auto] gap-2 items-center px-3 py-2 border-b border-slate-800/60 hover:bg-[#121822]/60 transition-colors"
                    data-testid="active-user-row"
                  >
                    {/* User info */}
                    <div className="flex items-center min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-200 text-xs font-medium truncate">
                            {s.name}
                          </span>
                          {s.session_count > 1 && (
                            <span
                              title={`${s.session_count} active sessions`}
                              className="flex-shrink-0 px-1 py-0.5 text-[9px] font-mono tracking-wide bg-orange-500/15 border border-orange-500/30 text-orange-400 leading-none"
                            >
                              ×{s.session_count}
                            </span>
                          )}
                        </div>
                        <div className="text-slate-500 text-[10px] truncate font-mono">
                          {s.email}
                        </div>
                      </div>
                    </div>

                    {/* Last seen / Expires */}
                    <div className="min-w-0">
                      <div className="text-slate-400 text-[10px] font-mono truncate">
                        {s.last_seen_at ? formatRelativeTime(s.last_seen_at) : formatDateTime(s.created_at)}
                      </div>
                      <div className="text-slate-600 text-[10px] font-mono truncate">
                        {s.last_seen_at ? "last seen" : "session start"}
                      </div>
                    </div>

                    {/* Kick button */}
                    <div className="w-16 flex justify-end">
                      <button
                        onClick={() => setKickTarget(s)}
                        disabled={s.email === user?.email}
                        title={s.email === user?.email ? "You cannot kick your own session" : `Kick ${s.name}`}
                        aria-label={s.email === user?.email ? "Cannot kick your own session" : `Kick ${s.name}`}
                        className="flex items-center gap-1 px-2 py-1.5 border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 hover:border-orange-500 transition-colors text-[10px] tracking-widest uppercase disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-orange-500/40"
                        data-testid="kick-user-button"
                      >
                        <UserX className="w-3 h-3" />
                        Kick
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-slate-800/60 flex-shrink-0">
            <div className="text-[10px] text-slate-600 tracking-wide">
              {sessions.length} active user{sessions.length !== 1 ? "s" : ""} (seen in last 5 min) — auto-refreshes every 5s
            </div>
          </div>
        </div>
      </SimpleDialog>

      {/* Clear All confirmation dialog */}
      {clearAllConfirm && (
        <SimpleDialog open={clearAllConfirm} onOpenChange={(v) => !v && setClearAllConfirm(false)} className="max-w-sm">
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 rounded-none text-white p-4"
            data-testid="clear-all-confirm-dialog"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
                Confirm Action
              </div>
              <div className="font-display text-xl tracking-tight mb-2">
                Clear all sessions?
              </div>
              <div className="text-slate-400 text-sm">
                This will immediately log out{" "}
                <span className="text-slate-200 font-medium">all {sessions.length} active session{sessions.length !== 1 ? "s" : ""}</span>.
                Every user will need to sign in again.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setClearAllConfirm(false)}
                disabled={clearingAll}
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40"
                data-testid="clear-all-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearAllConfirm}
                disabled={clearingAll}
                className="flex items-center gap-2 px-5 py-2 border border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
                data-testid="clear-all-confirm-submit"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {clearingAll ? "Clearing…" : "Clear All Sessions"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}

      {/* Kick confirmation dialog */}
      {kickTarget && (
        <SimpleDialog open={!!kickTarget} onOpenChange={(v) => !v && setKickTarget(null)} className="max-w-md">
          <div
            className="bg-[#0B0E14]/95 backdrop-blur-xl border border-orange-500/30 rounded-none text-white p-4"
            data-testid="kick-confirm-dialog"
          >
            <div className="mb-3">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
                Confirm Action
              </div>
              <div className="font-display text-xl tracking-tight mb-2">
                Kick this user?
              </div>
              <div className="text-slate-400 text-sm">
                This will immediately log out{" "}
                <span className="text-slate-200 font-medium">{kickTarget.name}</span>{" "}
                (<span className="font-mono text-slate-300">{kickTarget.email}</span>).
                They will need to sign in again.
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setKickTarget(null)}
                disabled={kicking}
                className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase disabled:opacity-40"
                data-testid="kick-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleKickConfirm}
                disabled={kicking}
                className="flex items-center gap-2 px-5 py-2 border border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
                data-testid="kick-confirm-submit"
              >
                <UserX className="w-3.5 h-3.5" />
                {kicking ? "Kicking…" : "Kick User"}
              </button>
            </div>
          </div>
        </SimpleDialog>
      )}
    </>
  );
}
